import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { LanguageProvider, useLanguage } from "./LanguageContext";
import { ThemeProvider, useTheme, PALETTES, PALETTE_KEYS } from "./ThemeContext";
import HistoryPage from "./pages/HistoryPage";
import TokenDesignPage from "./pages/TokenDesignPage";
import LandingPage from "./pages/LandingPage";
import { 
  Wallet, 
  ArrowRight, 
  Shield, 
  TrendingUp,
  Languages,
  BookOpen,
  Menu,
  X,
  Palette
} from "lucide-react";

// Palette Switcher Mini Component
const PaletteSwitcher = ({ className = "" }) => {
  const { paletteKey, setPaletteKey, palette } = useTheme();
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`relative ${className}`}>
      <button
        data-testid="palette-switcher-btn"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/30 hover:bg-white/10 transition-all"
      >
        <Palette className="w-4 h-4 text-white/80" />
        <div 
          className="w-4 h-4 rounded-full border border-white/50"
          style={{ background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})` }}
        />
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 bg-slate-900/95 backdrop-blur-xl rounded-2xl p-3 border border-white/10 min-w-[200px] shadow-2xl">
            <p className="text-white/50 text-xs font-medium px-2 mb-2 uppercase tracking-wider">
              {language === 'fr' ? 'Thème' : 'Theme'}
            </p>
            {PALETTE_KEYS.map((key) => {
              const p = PALETTES[key];
              return (
                <button
                  key={key}
                  data-testid={`palette-option-${key}`}
                  onClick={() => { setPaletteKey(key); setIsOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    paletteKey === key ? 'bg-white/15' : 'hover:bg-white/10'
                  }`}
                >
                  <div 
                    className={`w-5 h-5 rounded-full border-2 ${paletteKey === key ? 'border-white' : 'border-white/30'}`}
                    style={{ background: `linear-gradient(135deg, ${p.primary}, ${p.primaryDark})` }}
                  />
                  <span className={`text-sm ${paletteKey === key ? 'text-white font-medium' : 'text-white/70'}`}>
                    {language === 'fr' ? p.name.fr : p.name.en}
                  </span>
                  {key === 'quebec' && (
                    <span className="ml-auto text-[10px] text-white/40 uppercase tracking-wider">
                      {language === 'fr' ? 'défaut' : 'default'}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// Language Switcher for themed navbar
const LanguageSwitcherDark = () => {
  const { language, toggleLanguage } = useLanguage();
  
  return (
    <button
      data-testid="language-switcher"
      onClick={toggleLanguage}
      className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/30 hover:bg-white/10 transition-all"
    >
      <Languages className="w-4 h-4 text-white/80" />
      <span className="text-sm font-medium text-white/90 uppercase">
        {language === 'en' ? 'FR' : 'EN'}
      </span>
    </button>
  );
};

// Themed Navbar for sub-pages (Histoire, Token)
const ThemedNavbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language } = useLanguage();
  const { palette } = useTheme();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav 
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? `${palette.navSolid} backdrop-blur-lg shadow-lg` 
          : `${palette.navSolid} backdrop-blur-lg`
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
              style={{ 
                background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})`,
                boxShadow: `0 4px 15px ${palette.primary}30`
              }}
            >
              <span className="text-slate-900 font-bold text-lg">&#8369;</span>
            </div>
            <span className="font-outfit text-xl font-bold text-white tracking-tight">
              PIASTRE
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/histoire" 
              className="text-white/70 hover:text-white transition-colors font-medium flex items-center gap-1.5"
              data-testid="nav-history-link"
            >
              <BookOpen className="w-4 h-4" />
              {language === 'fr' ? "Histoire" : "History"}
            </Link>
            <Link 
              to="/token" 
              className="text-white/70 hover:text-white transition-colors font-medium"
              data-testid="nav-token-link"
            >
              Token
            </Link>
          </div>

          {/* Right Controls */}
          <div className="hidden md:flex items-center gap-3">
            <PaletteSwitcher />
            <LanguageSwitcherDark />
            <button 
              data-testid="nav-cta-btn"
              className="px-5 py-2.5 text-slate-900 rounded-full font-semibold text-sm transition-all"
              style={{ 
                background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})`,
                boxShadow: `0 4px 15px ${palette.primary}25`
              }}
            >
              {language === 'fr' ? 'Rejoindre' : 'Join Us'}
            </button>
          </div>

          {/* Mobile Menu */}
          <div className="flex items-center gap-2 md:hidden">
            <PaletteSwitcher />
            <LanguageSwitcherDark />
            <button 
              data-testid="mobile-menu-btn"
              className="p-2 text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/10" data-testid="mobile-menu">
            <div className="flex flex-col gap-3">
              <Link to="/" className="text-white/70 hover:text-white font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>
                {language === 'fr' ? 'Accueil' : 'Home'}
              </Link>
              <Link to="/histoire" className="text-white/70 hover:text-white font-medium py-2 flex items-center gap-2" onClick={() => setIsMobileMenuOpen(false)}>
                <BookOpen className="w-4 h-4" />
                {language === 'fr' ? "Histoire" : "History"}
              </Link>
              <Link to="/token" className="text-white/70 hover:text-white font-medium py-2" onClick={() => setIsMobileMenuOpen(false)}>
                Token Design
              </Link>
              <button 
                className="mt-2 px-6 py-3 text-slate-900 rounded-full font-semibold text-sm"
                style={{ background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})` }}
              >
                {language === 'fr' ? 'Rejoindre' : 'Join Us'}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Themed Footer for sub-pages
const ThemedFooter = () => {
  const { language } = useLanguage();
  const { palette } = useTheme();

  return (
    <footer data-testid="footer" className={`${palette.footerBg} py-12 border-t border-white/10`}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <Link to="/" className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${palette.primary}, ${palette.primaryDark})` }}
            >
              <span className="text-slate-900 font-bold">&#8369;</span>
            </div>
            <span className="text-white font-outfit text-xl font-bold">PIASTRE.ONE</span>
          </Link>

          <div className="flex gap-8 text-white/60">
            <Link to="/histoire" className="hover:text-white transition-colors">
              {language === 'fr' ? 'Histoire' : 'History'}
            </Link>
            <Link to="/token" className="hover:text-white transition-colors">
              Token
            </Link>
          </div>

          <div className="flex items-center gap-4 text-sm text-white/40">
            <span className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" style={{ color: palette.primary }} />
              Stellar
            </span>
            <span>&copy; 2026 Piastre.one</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Sub-page layout wrapper
const SubPageLayout = ({ children }) => {
  const { palette } = useTheme();
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: palette.lightPageBg }}>
      <ThemedNavbar />
      <div className="pt-16">
        {children}
      </div>
      <ThemedFooter />
    </div>
  );
};

function AppContent() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/histoire" element={
          <SubPageLayout>
            <HistoryPage />
          </SubPageLayout>
        } />
        <Route path="/history" element={
          <SubPageLayout>
            <HistoryPage />
          </SubPageLayout>
        } />
        <Route path="/token" element={
          <SubPageLayout>
            <TokenDesignPage />
          </SubPageLayout>
        } />
        <Route path="/token-design" element={
          <SubPageLayout>
            <TokenDesignPage />
          </SubPageLayout>
        } />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <AppContent />
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
